import React from "react";
import { useState, useEffect } from "react";
import EventDisplay from "../components/Cards/EventCard/EventDisplay";
import Header from "../components/Cards/CardComponents/Header";
import FAQSection from "../components/Common/FAQ";
import Testimonials from "../components/Common/Testimonials";
import SubscribeLandingpage from "../components/subscribe_landingpage";
import {
  ALLPUJA,
  ALLOFFERINGS,
  PUJAFAQ,
  OFFERINGFAQ,
  TESTIMONIAL,
} from "../rest/endpoints";
import ConditionalRenderer from "../devsathan_components/ConditionalRenderer";
import { useAuthHeaders } from "../utils/getHeaders";
import { apiRequest } from "../utils/apiRequest";
import { toast } from "react-toastify";
import LoadingIndicator from "../components/Common/Loading";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";

const EventListingPage = () => {
  const { eventtype } = useParams();

  const headers = useAuthHeaders();
  const [testimonials, setTestimonials] = useState([]);
  const [event, setEvent] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Infinite scroll states
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchMoreData = async () => {
    if (isLoadingMore) return;
    
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    
    try {
      const url = eventtype === "puja" ? ALLPUJA : ALLOFFERINGS;
      const result = await apiRequest({
        url: `${url}${nextPage}`,
        method: "GET",
        headers,
      });

      const newEvents = eventtype === "puja" 
        ? result?.data?.pujaList 
        : result?.data?.offeringList;

      if (newEvents && newEvents.length > 0) {
        setEvent(prevEvents => [...prevEvents, ...newEvents]);
        setCurrentPage(nextPage);
        
        // Check if there are more pages
        const totalPages = result?.data?.totalPages;
        if (nextPage >= totalPages - 1) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error(
        "Error fetching more events:",
        error.message || "Something went wrong"
      );
      toast.error("Error fetching more events. Please try again.");
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      setCurrentPage(0);
      setHasMore(true);
      
      const url = eventtype === "puja" ? ALLPUJA : ALLOFFERINGS;
      try {
        const result = await apiRequest({
          url: `${url}0`,
          method: "GET",
          headers,
        });

        const initialEvents = eventtype === "puja"
          ? result?.data?.pujaList
          : result?.data?.offeringList;

        setEvent(initialEvents || []);
        
        // Check if there are more pages
        const totalPages = result?.data?.totalPages;
        setHasMore(totalPages > 1);
      } catch (error) {
        console.error(
          "Error fetching initial events:",
          error.message || "Something went wrong"
        );
        toast.error("Error fetching events. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [eventtype]);


  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const result = await apiRequest({
          url: TESTIMONIAL,
          method: "GET",
          headers,
        });

        // Check if testimonial data is an array and update state
        if (result?.data?.reviews && Array.isArray(result.data.reviews)) {
          setTestimonials(result.data.reviews);
        } else {
          console.error("Testimonial data is not available or not an array");
          toast.error("Testimonial data is not available.");
        }
      } catch (error) {
        // Handle errors
        console.error(
          "Error fetching testimonials:",
          error.message || "Something went wrong"
        );
        toast.error("Error fetching testimonials. Please try again.");
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    const url = eventtype === "puja" ? PUJAFAQ : OFFERINGFAQ; // Adjust the URL based on event type
    const fetchfaq = async () => {
      try {
        const result = await apiRequest({
          url: url,
          method: "GET",
          headers,
        });

        // Check if FAQ data is an array and update state
        if (result?.data?.faqs && Array.isArray(result.data.faqs)) {
          setFaqs(result.data.faqs);
        } else {
          console.error("FAQ data is not available or not an array");
          toast.error("FAQ data is not available.");
        }
      } catch (error) {
        // Handle errors
        console.error(
          "Error fetching FAQs:",
          error.message || "Something went wrong"
        );
        toast.error("Error fetching FAQs. Please try again.");
      }
    };

    fetchfaq();
  }, [eventtype]);

  if (eventtype !== "puja" && eventtype !== "chadawa") {
    return;
  }

  return (
    <div
      key={eventtype}
      className="bg-[#FFECE5] min-h-screen pt-1 -mt-10 md:-mt-16"
    >
      <div className="max-w-full mx-auto px-6 sm:px-16 py-10 md:py-20">
        <div>
          <Header eventtype={eventtype} />
        </div>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <div>
            <InfiniteScroll
              dataLength={event.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <div className="flex justify-center py-4">
                  <LoadingIndicator />
                </div>
              }
              endMessage={
                <div className="text-center py-4 text-gray-600">
                  <p>You have seen all events!</p>
                </div>
              }
              scrollThreshold={0.5}
              className="overflow-x-auto whitespace-nowrap sm:-mt-20"
            >
              <EventDisplay
                events={event}
                eventtype={eventtype}
                viewType="list"
              />
            </InfiniteScroll>
          </div>
        )}
        <div>
          <ConditionalRenderer componentKey="SubscribeLandingpage">
            <SubscribeLandingpage />
          </ConditionalRenderer>
        </div>
        <div className="md:px-14 -mb-10 sm:-mt-10">
          <Testimonials testimonials={testimonials} />
        </div>

        <div className="mt-8 -mx-4 sm:mt-2 sm:-mb-10">
          <FAQSection faqs={faqs} />
        </div>
      </div>
    </div>
  );
};

export default EventListingPage;
